import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Check } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SPRING, DURATION, EASE } from "./tokens";

const states = {
  rest: { y: 0, scale: 1 },
  hover: { y: -1 },
  tap: { y: 0, scale: 0.975 },
};

const swap = {
  initial: { opacity: 0, y: 4 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

// Primary-CTA button with full state choreography:
// rest -> hover lift -> press compression -> loading spinner -> success check.
export const MotionButton = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      loading = false,
      success = false,
      successLabel = "Done",
      disabled = false,
      children,
      ...props
    },
    ref
  ) => {
    const locked = disabled || loading;
    return (
      <motion.button
        ref={ref}
        variants={states}
        initial="rest"
        whileHover={locked ? "rest" : "hover"}
        whileTap={locked ? "rest" : "tap"}
        transition={SPRING.micro}
        disabled={locked}
        className={cn(buttonVariants({ variant, size }), "relative overflow-hidden", className)}
        {...props}
      >
        <AnimatePresence mode="wait" initial={false}>
          {loading ? (
            <motion.span
              key="loading"
              {...swap}
              transition={{ duration: DURATION.fast, ease: EASE.standard }}
              className="inline-flex items-center gap-2"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              {children}
            </motion.span>
          ) : success ? (
            <motion.span
              key="success"
              {...swap}
              transition={{ duration: DURATION.fast, ease: EASE.enter }}
              className="inline-flex items-center gap-2"
            >
              <motion.span
                initial={{ scale: 0.4, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={SPRING.micro}
              >
                <Check className="h-4 w-4" />
              </motion.span>
              {successLabel}
            </motion.span>
          ) : (
            <motion.span
              key="content"
              {...swap}
              transition={{ duration: DURATION.fast, ease: EASE.standard }}
              className="inline-flex items-center gap-2"
            >
              {children}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    );
  }
);
MotionButton.displayName = "MotionButton";
